/**
 * Franja con la UF, la UTM, el dólar y el euro del día.
 *
 * Va en el encabezado, visible en toda la app: el contrato se cobra en UF y
 * se factura en pesos, así que el valor del día se consulta a cada rato y no
 * debería obligar a salir a buscarlo.
 *
 * Cuando el dato no es de hoy lo dice. Un número sin fecha invita a usarlo
 * creyendo que está al día, y en un estado de pago eso son millones.
 */

import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useIndicadores, type Indicador } from '@/hooks/useIndicadores';

const peso = (n: number, decimales = 2) =>
  n.toLocaleString('es-CL', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });

function Valor({
  nombre,
  dato,
  decimales,
}: {
  nombre: string;
  dato: Indicador | null;
  decimales?: number;
}) {
  if (!dato) return null;
  return (
    <span className="flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="text-[10px] uppercase tracking-wider text-texto-mute">{nombre}</span>
      <span className="font-mono text-xs tabular-nums text-texto">
        ${peso(dato.valor, decimales)}
      </span>
    </span>
  );
}

export function Indicadores() {
  const { indicadores, cargando, consultar, alDia } = useIndicadores();
  if (!indicadores) return null;

  const fecha = indicadores.uf?.fecha;
  const dia = fecha
    ? new Date(`${fecha}T00:00:00`).toLocaleDateString('es-CL', {
        day: '2-digit',
        month: '2-digit',
      })
    : null;

  return (
    <div className="border-b border-linea bg-petroleo-800/40">
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-x-5 gap-y-1 px-4 py-1.5">
        <Valor nombre="UF" dato={indicadores.uf} />
        <Valor nombre="UTM" dato={indicadores.utm} decimales={0} />
        <Valor nombre="Dólar" dato={indicadores.dolar} />
        <Valor nombre="Euro" dato={indicadores.euro} />

        <span
          className={cn(
            'ml-auto flex items-center gap-2 text-[10px]',
            alDia ? 'text-texto-mute' : 'text-fluor-amarillo',
          )}
        >
          {alDia ? `al ${dia}` : `último dato conocido · ${dia}`}
          <button
            type="button"
            onClick={() => void consultar()}
            disabled={cargando}
            aria-label="Actualizar los indicadores"
            title="Actualizar"
            className="text-texto-mute transition-colors hover:text-texto disabled:opacity-40"
          >
            <RefreshCw size={11} className={cargando ? 'animate-spin' : undefined} />
          </button>
        </span>
      </div>
    </div>
  );
}
